/**
 * <CommitPanel /> — Phase 7 commit surface.
 *
 * Commit runs in a background thread on the API (202 Accepted).
 * The dashboard polls session status until completed — large
 * worker imports must not block on a single HTTP request.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { api, type ImportSessionDTO } from "../../lib/api";

export function CommitPanel({ session }: { session: ImportSessionDTO }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [createContract, setCreateContract] = useState(true);
  const [seedLeaveBalances, setSeedLeaveBalances] = useState(false);

  const gateQ = useQuery({
    queryKey: ["import-commit-gate", session.id],
    queryFn: () => api.importCommitGate(session.id),
    refetchInterval: 3000,
  });

  const commit = useMutation({
    mutationFn: () =>
      api.importCommit(session.id, {
        create_contract_draft: createContract,
        seed_leave_balances: seedLeaveBalances,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["import-session", session.id] });
      void qc.invalidateQueries({ queryKey: ["import-rows", session.id] });
      void qc.invalidateQueries({ queryKey: ["import-rows-count", session.id] });
    },
    onError: () => {
      // A network drop may still have started the background commit —
      // refresh session status before showing a hard error.
      void qc.invalidateQueries({ queryKey: ["import-session", session.id] });
    },
  });
  const { reset: resetCommitMutation } = commit;

  const rowsQ = useQuery({
    queryKey: ["import-rows", session.id, "all"],
    queryFn: () =>
      api.importListRows(session.id, { status: "all", limit: 1000 }),
    refetchInterval: session.status === "committing" ? 3000 : 5000,
  });
  const breakdown = (() => {
    let c = 0, u = 0, un = 0;
    for (const r of rowsQ.data?.rows ?? []) {
      if (r.status === "error") continue;
      if (r.update_kind === "update") u++;
      else if (r.update_kind === "unchanged") un++;
      else c++;
    }
    return { c, u, un };
  })();

  if (session.status === "abandoned") {
    return null;
  }

  const isCompleted = session.status === "completed";
  const isCommitting = session.status === "committing";
  const isFailed = session.status === "failed";
  const gateAllowed = gateQ.data?.allowed ?? false;
  const gateReason = gateQ.data?.reason;

  // A proxy timeout ("failed to fetch") can fire after the API already
  // returned 202 and moved the session to committing — drop stale errors.
  useEffect(() => {
    if (isCommitting || isCompleted) {
      resetCommitMutation();
    }
  }, [isCommitting, isCompleted, resetCommitMutation]);

  const showCommitError =
    Boolean(commit.error) && !isCommitting && !isCompleted;

  return (
    <section className="rounded-xl border border-border-subtle bg-surface-default p-4 space-y-3">
      <header>
        <h3 className="text-sm font-semibold text-text-primary">
          {isCompleted ? t("app.cmp_complete_title") : t("app.cmp_ready_title")}
        </h3>
        <p className="mt-1 text-xs text-text-secondary">
          {isCompleted
            ? t("app.cmp_complete_body", {
                committed: session.total_rows_committed,
                pending: session.total_rows_pending,
              })
            : isCommitting
              ? t("app.cmp_commit_in_progress")
              : t("app.cmp_ready_body")}
        </p>
        {!isCompleted && !isCommitting && (breakdown.c + breakdown.u + breakdown.un) > 0 && (
          <div className="mt-2 flex items-center gap-3 text-xs flex-wrap">
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-800 px-2 py-0.5 font-medium">
              {t("app.cmp_pill_new", { n: breakdown.c })}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 text-blue-800 px-2 py-0.5 font-medium">
              {t("app.cmp_pill_updates", { n: breakdown.u })}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 text-text-secondary px-2 py-0.5 font-medium">
              {t("app.cmp_pill_unchanged", { n: breakdown.un })}
            </span>
          </div>
        )}
      </header>

      {isCommitting && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 rounded-md border border-brand-blue/30 bg-brand-blue/5 p-3 text-xs text-brand-deep">
            <Loader2 size={14} className="animate-spin shrink-0" aria-hidden="true" />
            <span>{t("app.cmp_commit_in_progress")}</span>
          </div>
          <button
            type="button"
            disabled={commit.isPending}
            onClick={() => commit.mutate()}
            className="inline-flex items-center gap-1 rounded-md border border-border-subtle bg-surface-default px-3 py-1.5 text-xs font-medium text-text-primary hover:bg-surface-subtle disabled:opacity-50"
          >
            {commit.isPending
              ? t("app.cmp_committing")
              : t("app.cmp_resume_commit")}
          </button>
        </div>
      )}

      {!isCompleted && (
        <>
          {!isCommitting && (
            <div className="space-y-2 text-xs">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={createContract}
                  onChange={(e) => setCreateContract(e.target.checked)}
                  className="rounded border-border-subtle"
                />
                <span className="text-text-primary">
                  {t("app.cmp_opt_contract")}
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={seedLeaveBalances}
                  onChange={(e) => setSeedLeaveBalances(e.target.checked)}
                  className="rounded border-border-subtle"
                />
                <span className="text-text-primary">
                  {t("app.cmp_opt_balances")}
                </span>
              </label>
            </div>
          )}

          {!gateAllowed && gateReason && !isCommitting && (
            <p className="text-xs text-amber-800 bg-amber-50 rounded p-2 border border-amber-200">
              {gateReason}
            </p>
          )}

          {isFailed && session.failure_reason && (
            <p className="text-xs text-rose-800 bg-rose-50 rounded p-2 border border-rose-200">
              {session.failure_reason}
            </p>
          )}

          {showCommitError && (
            <p className="text-xs text-rose-800 bg-rose-50 rounded p-2 border border-rose-200">
              {(commit.error as Error).message}
            </p>
          )}

          {!isCommitting && (
            <button
              type="button"
              disabled={!gateAllowed && !isFailed}
              onClick={() => commit.mutate()}
              className="inline-flex items-center gap-1 rounded-md bg-brand-blue px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {commit.isPending
                ? t("app.cmp_committing")
                : isFailed
                  ? t("app.cmp_retry_commit")
                  : t("app.cmp_commit_now")}
            </button>
          )}
        </>
      )}

      {isCompleted && (
        <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
          <Check size={16} className="text-emerald-700" />
          <span>
            {t("app.cmp_done_msg", { committed: session.total_rows_committed })}
          </span>
        </div>
      )}
    </section>
  );
}
